import { useQuery } from '@tanstack/react-query';
import { apiFetch, unwrapOne } from '@/utils/api';
import { qk } from '@/lib/qk';
import { useCurrentCompany } from '@/components/hooks/useApp';

/**
 * Uso del plan de la empresa activa (GET /api/plans/usage). SÍ tenant-scoped:
 * el queryKey incluye company.id y solo dispara con empresa activa.
 *
 * Respuesta (unwrapOne): {
 *   plan, label,
 *   limits: { invoices_per_year, ocr_per_month, banks, seats },  // number | null
 *   usage:  { invoices_year_used, ocr_month_used, banks_used, seats_used },
 *   credits: { granted, purchased, remaining, limit, used, expires_at },
 *   warnings: [{ resource, pct }]
 * }
 *
 * El consumo de IA NO está en `limits`/`usage`: dejó de ser un límite de plan
 * (`ai_per_month`, que contaba mensajes) y pasó a ser un SALDO de créditos
 * anclado al coste real del turno. No cabe en `limits` porque su techo depende
 * también de las recargas que compre el cliente, así que viaja en `credits`.
 * `warnings` sí puede traer `resource: 'ai_credits'` al 80 %.
 */
export function usePlanUsage() {
  const { data: company } = useCurrentCompany();

  const query = useQuery({
    queryKey: qk.planUsage(company?.id),
    queryFn: async () => unwrapOne(await apiFetch('/api/plans/usage')),
    enabled: !!company?.id,
    staleTime: 60 * 1000,
  });

  return {
    usage: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export default usePlanUsage;
